import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useBrand } from "../lib/BrandContext";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
];

function dashboardPathFor(user) {
  if (!user) return "/login";
  if (user.role === "coach") return "/coach/dashboard";
  if (user.role === "parent") return "/parent/dashboard";
  if (user.role === "platform_admin") return "/admin/dashboard";
  return "/";
}

export default function PublicLayout({ children }) {
  const brand = useBrand();
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // user === null while AuthContext is still checking /auth/me.
  // user === false when confirmed guest. object when signed in.
  const isAuthed = !!user && user !== false;
  const authResolved = !loading && user !== null;
  const dashPath = isAuthed ? dashboardPathFor(user) : null;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-ink-100 bg-white/80 backdrop-blur">
        <div className="container-page flex h-16 items-center justify-between gap-2">
          <Link to={isAuthed ? dashPath : "/"} className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900" data-testid="brand-home-link">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </span>
            {/* Wordmark stays hidden below 400px so the auth buttons never
                get squeezed off-screen on tiny phones (iPhone SE, etc.). */}
            <span className="hidden xs:inline">{brand.app_name}</span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors ${isActive ? "text-brand" : "text-ink-700 hover:text-ink-900"}`
                }
                data-testid={`public-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/*
              Auth CTAs are now visible on every viewport, not just desktop —
              returning coaches can tap Log in from the landing page without
              opening the menu. Buttons shrink on mobile via responsive
              height/padding/text-size. Anti-flicker placeholder preserved.
            */}
            {!authResolved ? (
              <div aria-hidden className="h-9 w-[140px] md:h-11 md:w-[200px]" />
            ) : isAuthed ? (
              <Link to={dashPath} data-testid="public-dashboard-link">
                <Button className="h-9 bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-hover md:h-11 md:px-5 md:text-base">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="public-login-link">
                  <Button
                    variant="outline"
                    className="h-9 border-ink-200 px-3 text-sm font-semibold text-ink-900 hover:bg-ink-50 md:h-11 md:px-5 md:text-base"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/signup" data-testid="public-signup-link">
                  <Button className="h-9 bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-hover md:h-11 md:px-5 md:text-base">
                    Start free
                  </Button>
                </Link>
              </>
            )}

            <button
              type="button"
              className="md:hidden ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-ink-100 text-ink-700"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open ? (
          <div className="md:hidden border-t border-ink-100 bg-white">
            <div className="container-page flex flex-col gap-3 py-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={`py-2 text-base font-semibold ${location.pathname === n.to ? "text-brand" : "text-ink-700"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </header>

      <main>{children}</main>

      <footer className="border-t border-ink-100 bg-white">
        <div className="container-page py-12">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 font-heading text-lg font-bold text-ink-900">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                {brand.app_name}
              </div>
              <p className="mt-3 text-sm text-ink-500">{brand.tagline}</p>
            </div>
            <div>
              <p className="label-caps">Product</p>
              <ul className="mt-3 space-y-2 text-sm text-ink-700">
                <li><Link to="/features" className="hover:text-ink-900">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-ink-900">Pricing</Link></li>
                <li><Link to="/faq" className="hover:text-ink-900">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <p className="label-caps">Account</p>
              <ul className="mt-3 space-y-2 text-sm text-ink-700">
                <li><Link to="/login" className="hover:text-ink-900">Log in</Link></li>
                <li><Link to="/signup" className="hover:text-ink-900">Start free</Link></li>
                <li><Link to="/forgot-password" className="hover:text-ink-900">Forgot password</Link></li>
              </ul>
            </div>
            <div>
              <p className="label-caps">Contact</p>
              <p className="mt-3 text-sm text-ink-700" data-testid="footer-contact">
                <a href="mailto:support@squadfee.com" className="hover:text-ink-900">support@squadfee.com</a>
              </p>
              <ul className="mt-3 space-y-2 text-sm text-ink-700">
                <li><Link to="/terms" className="hover:text-ink-900" data-testid="footer-terms-link">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-ink-900" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              </ul>
              <p className="mt-2 text-xs text-ink-500">© {new Date().getFullYear()} {brand.app_name}. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
